import React, { useEffect, useState } from "react";
import {
  fetchContacts,
  createContact,
  updateContact,
  deleteContact,
} from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Plus,
  Trash2,
  Mail,
  UserPlus,
  Pencil,
  Check,
  X,
  Users,
} from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "rep", label: "Sales rep" },
  { value: "driver", label: "Bus driver" },
  { value: "host", label: "Host" },
  { value: "lead_host", label: "Lead host" },
  { value: "other", label: "Other" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Contacts manager for the email (mailto:) route dispatch feature.
 * Users add their reps and bus drivers here — email is the primary
 * dispatch channel; phone is optional and kept for reference.
 */
export function ContactsManager() {
  const [contacts, setContacts] = useState(null);
  const [editing, setEditing] = useState(null); // contact being edited (or "new")
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "rep" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const list = await fetchContacts();
      setContacts(list);
    } catch {
      setContacts([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing("new");
    setForm({ name: "", email: "", phone: "", role: "rep" });
  };
  const startEdit = (c) => {
    setEditing(c.id);
    setForm({
      name: c.name,
      email: c.email || "",
      phone: c.phone || "",
      role: c.role,
    });
  };
  const cancel = () => {
    setEditing(null);
    setForm({ name: "", email: "", phone: "", role: "rep" });
  };

  const save = async () => {
    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    if (!name) {
      toast.error("Name is required");
      return;
    }
    if (!email) {
      toast.error("Email is required to send routes");
      return;
    }
    if (!EMAIL_RE.test(email)) {
      toast.error("That email address doesn't look valid");
      return;
    }
    try {
      setSaving(true);
      const payload = { name, email, phone, role: form.role };
      if (editing === "new") {
        await createContact(payload);
        toast.success(`Added ${name}`);
      } else {
        await updateContact(editing, payload);
        toast.success("Contact updated");
      }
      cancel();
      load();
    } catch (e) {
      toast.error("Could not save contact", { description: String(e) });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove ${c.name}?`)) return;
    try {
      await deleteContact(c.id);
      toast.success(`Removed ${c.name}`);
      load();
    } catch {
      toast.error("Could not remove contact");
    }
  };

  return (
    <div id="contacts" className="panel p-6 space-y-4 scroll-mt-20">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-sky-500/10 border border-sky-500/30 flex items-center justify-center">
              <Users className="w-4 h-4 text-sky-300" />
            </div>
            <h3 className="text-base font-medium text-zinc-50">
              Route Email Contacts
            </h3>
          </div>
          <p className="text-xs text-zinc-500 mt-1.5 max-w-md leading-relaxed">
            Saved email addresses for your sales reps and bus drivers. The{" "}
            <span className="inline-flex items-center gap-1 text-emerald-300">
              <Mail className="w-3 h-3" /> Email route
            </span>{" "}
            button on any route or lead list opens your device's default
            mail app pre-filled with the Google Maps link. Works on Windows,
            Mac, iPad &amp; mobile — no email service charges.
          </p>
        </div>
        {editing !== "new" && (
          <Button
            data-testid="add-contact-btn"
            onClick={startNew}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 rounded-md text-xs"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Add contact
          </Button>
        )}
      </div>

      {editing === "new" && (
        <ContactForm
          form={form}
          setForm={setForm}
          onSave={save}
          onCancel={cancel}
          saving={saving}
          mode="new"
        />
      )}

      <div className="divide-y divide-zinc-800 border border-zinc-800 rounded-md overflow-hidden">
        {contacts === null ? (
          <div className="px-4 py-6 text-xs text-zinc-500">Loading…</div>
        ) : contacts.length === 0 && editing !== "new" ? (
          <div className="px-4 py-8 text-center text-xs text-zinc-500">
            <UserPlus className="w-5 h-5 mx-auto text-zinc-700 mb-2" />
            No contacts yet — add your sales reps and bus drivers here.
          </div>
        ) : (
          contacts.map((c) =>
            editing === c.id ? (
              <ContactForm
                key={c.id}
                form={form}
                setForm={setForm}
                onSave={save}
                onCancel={cancel}
                saving={saving}
                mode="edit"
              />
            ) : (
              <div
                key={c.id}
                data-testid={`contact-row-${c.id}`}
                className="grid grid-cols-12 gap-3 items-center px-4 py-2.5 hover:bg-zinc-900/40 transition-colors"
              >
                <div className="col-span-1">
                  <span
                    className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                      c.role === "rep"
                        ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30"
                        : c.role === "driver"
                        ? "bg-sky-500/15 text-sky-200 border-sky-500/30"
                        : "bg-zinc-500/15 text-zinc-200 border-zinc-500/30"
                    }`}
                  >
                    {c.role}
                  </span>
                </div>
                <div className="col-span-3 text-sm text-zinc-100 truncate">{c.name}</div>
                <div className="col-span-4 text-xs font-mono text-zinc-300 truncate">
                  {c.email || (
                    <span className="text-rose-400/70 italic">no email — add one to dispatch</span>
                  )}
                </div>
                <div className="col-span-2 text-xs font-mono text-zinc-500 truncate">
                  {c.phone || <span className="text-zinc-700">—</span>}
                </div>
                <div className="col-span-2 flex justify-end gap-1">
                  <button
                    data-testid={`edit-contact-${c.id}`}
                    onClick={() => startEdit(c)}
                    className="text-zinc-500 hover:text-emerald-300 p-1.5 rounded hover:bg-zinc-800"
                    title="Edit"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    data-testid={`delete-contact-${c.id}`}
                    onClick={() => remove(c)}
                    className="text-zinc-500 hover:text-rose-400 p-1.5 rounded hover:bg-zinc-800"
                    title="Remove"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )
          )
        )}
      </div>
    </div>
  );
}

function ContactForm({ form, setForm, onSave, onCancel, saving, mode }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-center bg-emerald-500/5 border border-emerald-500/30 rounded-md p-2.5">
      <div className="col-span-2">
        <Select
          value={form.role}
          onValueChange={(v) => setForm({ ...form, role: v })}
        >
          <SelectTrigger
            data-testid="contact-form-role"
            className="bg-zinc-950 border-zinc-800 text-zinc-100 h-9 text-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            {ROLES.map((r) => (
              <SelectItem key={r.value} value={r.value}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Input
        data-testid="contact-form-name"
        placeholder="Name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        className="col-span-3 bg-zinc-950 border-zinc-800 text-zinc-100 h-9 text-sm"
      />
      <Input
        data-testid="contact-form-email"
        type="email"
        placeholder="driver@example.com"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        className="col-span-3 bg-zinc-950 border-zinc-800 text-zinc-100 h-9 text-sm font-mono"
      />
      <Input
        data-testid="contact-form-phone"
        placeholder="+1 555 0101 (optional)"
        value={form.phone}
        onChange={(e) => setForm({ ...form, phone: e.target.value })}
        className="col-span-2 bg-zinc-950 border-zinc-800 text-zinc-100 h-9 text-sm font-mono"
      />
      <div className="col-span-2 flex justify-end gap-1">
        <Button
          data-testid={mode === "new" ? "contact-save-new" : "contact-save-edit"}
          size="icon"
          onClick={onSave}
          disabled={saving}
          className="h-9 w-9 bg-emerald-600 hover:bg-emerald-500"
        >
          <Check className="w-4 h-4" />
        </Button>
        <Button
          size="icon"
          variant="outline"
          onClick={onCancel}
          className="h-9 w-9 border-zinc-800 bg-zinc-950 hover:bg-zinc-900"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default ContactsManager;
